import { useState } from 'react';
import { Button } from './Button';
import { IconCheck } from '../icons';

/**
 * Copy one value to the clipboard, and say so ("Copied") for a moment, politely announced.
 *
 * A clipboard write can be refused (permissions, an insecure origin, no clipboard at all): then
 * the value is offered in the browser's own prompt to copy by hand (`promptLabel`), never a
 * "Copied" that copied nothing. Shared by the team invite's link and "Your domain"'s DNS records
 * (MAP 249 ticket 08), so one fix reaches both.
 */
export function CopyButton({
  value,
  label = 'Copy',
  ariaLabel,
  promptLabel = 'Copy this',
  size = 'md',
}: {
  value: string;
  label?: string;
  /** What is copied, for a screen reader when the visible word is just "Copy". */
  ariaLabel?: string;
  /** The prompt's words when the clipboard refuses. */
  promptLabel?: string;
  size?: 'md' | 'sm';
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size={size}
      aria-label={ariaLabel}
      onClick={() => {
        // A missing clipboard throws at once; a refused one rejects: both are the same "no".
        void Promise.resolve()
          .then(() => navigator.clipboard.writeText(value))
          .then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            },
            // Saying "Copied" when nothing was copied is the kind of small lie that costs an
            // hour.
            () => window.prompt(promptLabel, value),
          );
      }}
    >
      <span aria-live="polite" className="inline-flex items-center gap-1">
        {copied ? (
          <>
            <IconCheck size={14} /> Copied
          </>
        ) : (
          label
        )}
      </span>
    </Button>
  );
}
